import React from 'react';
import { InputContainer, InputLabel, InputError, InputField } from './InputBase';

interface TextInputProps {
  id: string;
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  name?: string;
  autoComplete?: string;
  ariaLabel?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  min?: string | number;
  max?: string | number;
  maxLength?: React.InputHTMLAttributes<HTMLInputElement>['maxLength'];
  step?: string | number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoCorrect?: React.InputHTMLAttributes<HTMLInputElement>['autoCorrect'];
  autoCapitalize?: React.InputHTMLAttributes<HTMLInputElement>['autoCapitalize'];
  spellCheck?: React.InputHTMLAttributes<HTMLInputElement>['spellCheck'];
  size?: 'md' | 'sm';
}

const TextInput: React.FC<TextInputProps> = ({
  id,
  label,
  value,
  onChange,
  inputRef,
  autoFocus = false,
  name,
  type = 'text',
  placeholder,
  required = false,
  error,
  className = '',
  inputClassName = '',
  disabled = false,
  autoComplete,
  ariaLabel,
  onKeyDown,
  onBlur,
  onFocus,
  min,
  max,
  maxLength,
  step,
  inputMode,
  autoCorrect,
  autoCapitalize,
  spellCheck,
  size = 'md',
}) => {
  const errorId = error ? `${id}-error` : undefined;

  return (
    <InputContainer className={className}>
      <InputLabel label={label} required={required} htmlFor={id} />
      <InputField
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        className={inputClassName}
        disabled={disabled}
        initialFocus={autoFocus}
        ariaLabel={ariaLabel || (typeof label === 'string' ? label : placeholder)}
        autoComplete={autoComplete}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        min={min}
        max={max}
        maxLength={maxLength}
        step={step}
        inputMode={inputMode}
        autoCorrect={autoCorrect}
        autoCapitalize={autoCapitalize}
        spellCheck={spellCheck}
        size={size}
        aria-describedby={errorId}
        aria-invalid={Boolean(error)}
      />
      <InputError id={errorId} error={error} />
    </InputContainer>
  );
};

export default TextInput;
